import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../components/ProtectedRoute';
import PlanSummaryPage from '../pages/plan-summary/page';
import AboutPage from '../pages/about/page';
import ContactPage from '../pages/contact/page';
import PrivacyPage from '../pages/privacy/page';
import TermsPage from '../pages/terms/page';
import VerifyOTPPage from '../pages/verify-otp/page';
import ForgotPasswordPage from '../pages/forgot-password/page';
import ResetPasswordPage from '../pages/reset-password/page';
import VerifyEmailPage from '../pages/verify-email/page';
import SuspendedPage from '../pages/suspended/page';

// ROBUST DYNAMIC IMPORT WRAPPER
// This catches ERR_CONNECTION_TIMED_OUT (device woke from sleep) 
// and chunk errors (Vercel deployed a new version).
const retryLoadComponent = (fn: () => Promise<any>, retriesLeft = 3, interval = 1000): Promise<any> => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            // Out of retries. Force a hard reload of the current URL.
            window.location.reload();
            return reject(error);
          }
          // Network might just be waking up. Try again.
          retryLoadComponent(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
};

// LAZY LOADED ROUTES
// Wrapped with the retry logic to prevent SPA navigation crashes
const HomePage = lazy(() => retryLoadComponent(() => import('../pages/home/page')));
const RegisterPage = lazy(() => retryLoadComponent(() => import('../pages/register/page')));
const LoginPage = lazy(() => retryLoadComponent(() => import('../pages/login/page')));
const OnboardingPage = lazy(() => retryLoadComponent(() => import('../pages/onboarding/page')));
const DashboardPage = lazy(() => retryLoadComponent(() => import('../pages/dashboard/page')));
const NutritionPlanPage = lazy(() => retryLoadComponent(() => import('../pages/nutrition-plan/page')));
const RecipesPage = lazy(() => retryLoadComponent(() => import('../pages/recipes/page')));
const ShoppingListPage = lazy(() => retryLoadComponent(() => import('../pages/shopping-list/page')));
const ProgressPage = lazy(() => retryLoadComponent(() => import('../pages/progress/page')));
const ProfilePage = lazy(() => retryLoadComponent(() => import('../pages/profile/page')));
const ProductSettingsPage = lazy(() => retryLoadComponent(() => import('../pages/product-settings/page')));
const NotFoundPage = lazy(() => retryLoadComponent(() => import('../pages/NotFound')));
const SubscribePage = lazy(() => retryLoadComponent(() => import('../pages/subscribe/page')));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/about',
    element: <AboutPage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmailPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/verify-otp',
    element: <VerifyOTPPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  // Not wrapped in ProtectedRoute — suspended users must be able to reach this
  // without triggering the auth redirect loop. The page itself handles auth state.
  {
    path: '/suspended',
    element: <SuspendedPage />,
  },
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute requireOnboarding={false}>
        <OnboardingPage />
      </ProtectedRoute>
    ),
  },
  // Subscribe — requires auth + onboarding but NOT subscription (this is where user gets one)
  {
    path: '/subscribe',
    element: (
      <ProtectedRoute requireSubscription={false}>
        <SubscribePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <DashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/nutrition-plan',
    element: (
      <ProtectedRoute>
        <NutritionPlanPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/recipes',
    element: (
      <ProtectedRoute>
        <RecipesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/shopping-list',
    element: (
      <ProtectedRoute>
        <ShoppingListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/progress',
    element: (
      <ProtectedRoute>
        <ProgressPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/profile',
    element: (
      <ProtectedRoute>
        <ProfilePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/product-settings',
    element: (
      <ProtectedRoute>
        <ProductSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/plan-summary/:planId',
    element: <ProtectedRoute><PlanSummaryPage /></ProtectedRoute>,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;